import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import {
  useUserPermissions,
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
} from "../hooks/useUserPermissions";
import { PERMISSIONS } from "../utils/permissions";

interface PermissionContextType {
  // Check if the current user has a specific permission
  hasPermission: (permissionId: number) => boolean;

  // Check if the current user has any of the specified permissions
  hasAnyPermission: (permissionIds: number[]) => boolean;

  // Check if the current user has all of the specified permissions
  hasAllPermissions: (permissionIds: number[]) => boolean;

  // User's role information
  userRole: string | null;

  // Check if permissions are still loading
  isLoading: boolean;

  // Refresh permissions (e.g., after a role change)
  refreshPermissions: () => void;
}

const PermissionContext = createContext<PermissionContextType>({
  hasPermission: () => false,
  hasAnyPermission: () => false,
  hasAllPermissions: () => false,
  userRole: null,
  isLoading: true,
  refreshPermissions: () => {},
});

export const usePermissions = () => useContext(PermissionContext);

export const PermissionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { data: user, isLoading: isUserLoading } = useAuth();
  const [roleName, setRoleName] = useState<string | null>(null);

  // Use the new hook to get permissions with auto-refresh
  const {
    data: permissionsData,
    isLoading: isPermissionsLoading,
    refetch: refetchPermissions,
  } = useUserPermissions(user?.id);

  // Effect to update role name when user data or permissions data changes
  useEffect(() => {
    // Extract role name from permissions data or fall back to user data
    const getRoleName = () => {
      // If user is null or undefined, return null
      if (!user) return null;

      // If permissions data is available, use role name from there
      if (
        permissionsData &&
        permissionsData.role &&
        permissionsData.role.name
      ) {
        return permissionsData.role.name;
      }

      // Case 1: From login response structure (from API)
      if (user.roles && user.roles.name) {
        return user.roles.name;
      }

      // Case 2: From useAuth.ts structure (for backward compatibility)
      if (user.roleInfo && user.roleInfo.role && user.roleInfo.role.name) {
        return user.roleInfo.role.name;
      }

      // Case 3: Fall back to user type name if available
      if (user.user_type && user.user_type.name) {
        return user.user_type.name;
      }

      // Final fallback
      return "Unknown Role";
    };

    setRoleName(getRoleName());
  }, [user, permissionsData]);

  // Check if the user has a specific permission by ID
  const checkHasPermission = (permissionId: number): boolean => {
    // First try to use the fresh permissions from the API
    if (permissionsData && permissionsData.permissions) {
      return hasPermission(permissionsData.permissions, permissionId);
    }

    // Fall back to permissions from direct API response
    if (user && user.roles && user.roles.permissions) {
      // Convert role permissions array to a map for checking
      const permissions: Record<number, boolean> = {};
      user.roles.permissions.forEach((perm) => {
        permissions[perm.permission_id] = perm.status === 1;
      });
      return hasPermission(permissions, permissionId);
    }

    // Fall back to roleInfo permissions from cached user
    if (user && user.roleInfo && user.roleInfo.permissions) {
      return user.roleInfo.permissions[permissionId] === true;
    }

    return false;
  };

  // Check if the user has any of the provided permissions
  const checkHasAnyPermission = (permissionIds: number[]): boolean => {
    // First try to use the fresh permissions from the API
    if (permissionsData && permissionsData.permissions) {
      return hasAnyPermission(permissionsData.permissions, permissionIds);
    }

    // Fall back to permissions from direct API response
    if (user && user.roles && user.roles.permissions) {
      // Convert role permissions array to a map for checking
      const permissions: Record<number, boolean> = {};
      user.roles.permissions.forEach((perm) => {
        permissions[perm.permission_id] = perm.status === 1;
      });
      return hasAnyPermission(permissions, permissionIds);
    }

    // Fall back to roleInfo permissions from cached user
    if (user && user.roleInfo && user.roleInfo.permissions) {
      return permissionIds.some(
        (id) => user.roleInfo!.permissions[id] === true
      );
    }

    return false;
  };

  // Check if the user has all of the provided permissions
  const checkHasAllPermissions = (permissionIds: number[]): boolean => {
    // First try to use the fresh permissions from the API
    if (permissionsData && permissionsData.permissions) {
      return hasAllPermissions(permissionsData.permissions, permissionIds);
    }

    // Fall back to permissions from direct API response
    if (user && user.roles && user.roles.permissions) {
      // Convert role permissions array to a map for checking
      const permissions: Record<number, boolean> = {};
      user.roles.permissions.forEach((perm) => {
        permissions[perm.permission_id] = perm.status === 1;
      });
      return hasAllPermissions(permissions, permissionIds);
    }

    // Fall back to roleInfo permissions from cached user
    if (user && user.roleInfo && user.roleInfo.permissions) {
      return permissionIds.every(
        (id) => user.roleInfo!.permissions[id] === true
      );
    }

    return false;
  };

  // Function to manually refresh permissions
  const refreshPermissions = () => {
    refetchPermissions();
  };

  return (
    <PermissionContext.Provider
      value={{
        hasPermission: checkHasPermission,
        hasAnyPermission: checkHasAnyPermission,
        hasAllPermissions: checkHasAllPermissions,
        userRole: roleName,
        isLoading: isUserLoading || isPermissionsLoading,
        refreshPermissions,
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
};

// Export PERMISSIONS so they can be imported directly from this file
export { PERMISSIONS };
